import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import { DECKS } from '../data/stimuli';
import { MATCH_TYPES } from '../data/matching';
import '../styles/sequencing-generator.css';
import '../styles/curriculum-manager.css';

function genId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Turn a chosen set of cards into ready-to-review matching trials ──────────
// Every image used here was already deliberately chosen by an admin when the
// deck was built — this only recombines existing assets, it never sources or
// picks new imagery on its own.

function buildTrialsFromCards(cards, { choiceCount, nonIdentical }) {
  const effChoiceCount = Math.min(choiceCount, cards.length);
  if (effChoiceCount < 2) return [];

  return cards.map((card, idx) => {
    const distractors = shuffle(cards.filter((_, i) => i !== idx)).slice(0, effChoiceCount - 1);
    const correctImage = (nonIdentical && card.variations?.length) ? card.variations[0] : card.image;
    const media = [...new Set([card.image, ...(card.variations || [])])];

    return {
      id: genId('t'),
      order: idx + 1,
      prompt: { label: card.label, imageUrl: card.image, media },
      choices: [
        { id: genId('c'), label: card.label, imageUrl: correctImage, isCorrect: true },
        ...distractors.map(d => ({ id: genId('c'), label: d.label, imageUrl: d.image, isCorrect: false })),
      ],
    };
  });
}

// ══════════════════════════════════════════════════════════════════════════════
// Generate a Matching Activity from an existing deck — identical / non-identical
// (generalization) matching only. Creates a draft; nothing is published without
// review. No difficulty setting — that's a sequencing-activity concept that
// doesn't map onto matching, which is scoped entirely by which cards you pick.
// ══════════════════════════════════════════════════════════════════════════════

export const MatchingGenerateFromDeckPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [selectedDeck,   setSelectedDeck]   = useState(null);
  const [selectedLabels, setSelectedLabels] = useState(null); // null until a deck is picked
  const [choiceCount,    setChoiceCount]    = useState(3);
  const [nonIdentical,   setNonIdentical]   = useState(true);
  const [generating,     setGenerating]     = useState(false);
  const [error,          setError]          = useState(null);

  const eligibleItems = selectedDeck ? selectedDeck.items.filter(c => c.image) : [];

  // Default to every eligible card selected whenever a new deck is chosen
  useEffect(() => {
    setSelectedLabels(selectedDeck ? new Set(eligibleItems.map(c => c.label)) : null);
    setError(null);
  }, [selectedDeck]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCards = eligibleItems.filter(c => selectedLabels?.has(c.label));
  const canGenerate = selectedCards.length >= 2;

  const toggleCard = (label) => {
    setSelectedLabels(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  const selectAll = () => setSelectedLabels(new Set(eligibleItems.map(c => c.label)));
  const clearAll  = () => setSelectedLabels(new Set());

  const handleGenerate = async () => {
    if (!selectedDeck || !canGenerate || generating) return;
    setGenerating(true);
    setError(null);

    try {
      const trials = buildTrialsFromCards(selectedCards, { choiceCount, nonIdentical });
      if (trials.length === 0) {
        setError('Pick at least 2 cards to build a matching activity.');
        setGenerating(false);
        return;
      }

      const docRef = await addDoc(collection(db, 'generatedMatchingDecks'), {
        createdBy:   currentUser.uid,
        status:      'draft',
        title:       selectedDeck.name,
        description: '',
        category:    selectedDeck.name,
        subcategory: '',
        matchType:   MATCH_TYPES.IDENTICAL,
        tags:        [selectedDeck.name.toLowerCase(), 'auto-generated'],
        trials,
        trialCount:  trials.length,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });

      navigate(`/matching/review/${docRef.id}`);
    } catch (err) {
      setError(err.message || 'Could not generate the activity. Please try again.');
      setGenerating(false);
    }
  };

  return (
    <Layout>
      <div className="gen-page">

        <div className="gen-header">
          <div className="gen-header-icon">⚡</div>
          <div>
            <h1 className="gen-title">Generate Matching Activity From a Deck</h1>
            <p className="gen-subtitle">
              Builds matching trials straight from an existing deck's own images — nothing new
              is uploaded or auto-picked. You'll still review before publishing.
            </p>
          </div>
        </div>

        <div className="gen-input-card">
          <label className="gen-label">Choose a deck</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
            {DECKS.map(deck => {
              const count = deck.items.filter(c => c.image).length;
              const selected = selectedDeck?.name === deck.name;
              return (
                <button
                  key={deck.name}
                  onClick={() => setSelectedDeck(deck)}
                  className={`gen-diff-btn ${selected ? 'gen-diff-btn--on' : ''}`}
                  style={{ '--diff-color': '#8B2635' }}
                >
                  <img
                    src={deck.coverImage} alt=""
                    style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover' }}
                  />
                  <span className="gen-diff-label">{deck.name}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>{count} cards</span>
                </button>
              );
            })}
          </div>

          {selectedDeck && (
            <>
              {/* ── Which items to match ─────────────────────────────────── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className="gen-label" style={{ marginBottom: 0 }}>Choose items to match</label>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#8B2635' }}>
                  {selectedCards.length} / {eligibleItems.length} selected
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button type="button" onClick={selectAll} className="cm-btn" style={{ fontSize: 12 }}>Select All</button>
                <button type="button" onClick={clearAll} className="cm-btn" style={{ fontSize: 12 }}>Clear All</button>
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8,
                maxHeight: 280, overflowY: 'auto', padding: 10, border: '1.5px solid #E5E7EB',
                borderRadius: 12, marginBottom: 20, background: '#F9FAFB',
              }}>
                {eligibleItems.map(card => {
                  const isSelected = selectedLabels?.has(card.label);
                  return (
                    <button
                      key={card.label}
                      type="button"
                      onClick={() => toggleCard(card.label)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                        padding: 6, borderRadius: 10, border: '2px solid',
                        borderColor: isSelected ? '#8B2635' : 'transparent',
                        background: '#fff', cursor: 'pointer', position: 'relative',
                        opacity: isSelected ? 1 : 0.5,
                      }}
                    >
                      <div style={{ width: '100%', aspectRatio: '1 / 1', borderRadius: 8, overflow: 'hidden', background: '#F3F4F6' }}>
                        <img src={card.image} alt={card.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', textAlign: 'center' }}>{card.label}</span>
                      {isSelected && (
                        <span style={{
                          position: 'absolute', top: 4, right: 4, width: 16, height: 16, borderRadius: '50%',
                          background: '#8B2635', color: '#fff', fontSize: 9, display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                        }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* ── Identical vs. non-identical ──────────────────────────── */}
              <label className="gen-label">Matching Type</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                <button
                  type="button"
                  onClick={() => setNonIdentical(false)}
                  style={{
                    flex: 1, padding: '12px 14px', borderRadius: 12, border: '1.5px solid',
                    borderColor: !nonIdentical ? '#8B2635' : '#E5E7EB',
                    background: !nonIdentical ? '#8B263518' : '#F9FAFB',
                    color: !nonIdentical ? '#8B2635' : '#6B7280',
                    fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Identical</div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>Match the exact same photo.</div>
                </button>
                <button
                  type="button"
                  onClick={() => setNonIdentical(true)}
                  style={{
                    flex: 1, padding: '12px 14px', borderRadius: 12, border: '1.5px solid',
                    borderColor: nonIdentical ? '#8B2635' : '#E5E7EB',
                    background: nonIdentical ? '#8B263518' : '#F9FAFB',
                    color: nonIdentical ? '#8B2635' : '#6B7280',
                    fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Non-Identical</div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>Match a different photo of the same item, when one exists.</div>
                </button>
              </div>

              {/* ── How many cards to display per trial ──────────────────── */}
              <label className="gen-label">Cards to Display per Trial</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 6 }}>
                <button
                  onClick={() => setChoiceCount(n => Math.max(2, n - 1))}
                  disabled={choiceCount <= 2}
                  aria-label="Fewer cards"
                  style={{
                    width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #E5E7EB',
                    background: '#F9FAFB', fontSize: 20, fontWeight: 700, color: '#374151',
                    cursor: choiceCount <= 2 ? 'not-allowed' : 'pointer',
                    opacity: choiceCount <= 2 ? 0.4 : 1,
                  }}
                >−</button>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#111827', minWidth: 32, textAlign: 'center' }}>
                  {choiceCount}
                </span>
                <button
                  onClick={() => setChoiceCount(n => Math.min(4, n + 1))}
                  disabled={choiceCount >= 4}
                  aria-label="More cards"
                  style={{
                    width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #E5E7EB',
                    background: '#F9FAFB', fontSize: 20, fontWeight: 700, color: '#374151',
                    cursor: choiceCount >= 4 ? 'not-allowed' : 'pointer',
                    opacity: choiceCount >= 4 ? 0.4 : 1,
                  }}
                >+</button>
              </div>
              <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 28px' }}>
                How many cards (the correct match plus distractors) show per trial.
              </p>

              <div style={{
                background: '#F9FAFB', border: '1.5px solid #E5E7EB', borderRadius: 12,
                padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#374151',
              }}>
                Will create <strong>{selectedCards.length}</strong> trial{selectedCards.length !== 1 ? 's' : ''} from your
                selected cards in <strong>{selectedDeck.name}</strong>, each showing {Math.min(choiceCount, selectedCards.length)} cards.
              </div>

              {!canGenerate && (
                <p className="gen-error">Pick at least 2 cards to continue.</p>
              )}
              {error && <p className="gen-error">{error}</p>}

              <button
                className="gen-cta"
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
              >
                {generating
                  ? <><span className="gen-spinner" /> Generating…</>
                  : '⚡ Generate Draft & Review'
                }
              </button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
